import { useState } from 'react'
import { router } from '@inertiajs/react'
import { CheckCircle, XCircle, MinusCircle } from 'lucide-react'

interface Props {
  won_the_week: boolean | null
  win_count: number
  total_count: number
  win_mode: 'automatic' | 'manual'
  win_threshold: number
  meeting_id?: number
  can_facilitate?: boolean
}

export default function WinTheWeekBanner({ won_the_week, win_count, total_count, win_mode, win_threshold, meeting_id, can_facilitate }: Props) {
  const [saving, setSaving] = useState(false)

  const isWin = won_the_week === true
  const isLoss = won_the_week === false
  const isNeutral = won_the_week === null

  const borderColor = isWin ? '#15532E' : isLoss ? '#ED1C24' : '#6B5B5A'
  const bgColor = isWin ? 'rgba(21,83,46,0.08)' : isLoss ? 'rgba(237,28,36,0.08)' : 'rgba(107,91,90,0.06)'
  const textColor = isWin ? '#15532E' : isLoss ? '#ED1C24' : '#1B1B1B'

  const Icon = isWin ? CheckCircle : isLoss ? XCircle : MinusCircle

  const label = isWin
    ? 'We Won the Week!'
    : isLoss
      ? 'We Did Not Win the Week'
      : win_mode === 'manual'
        ? 'Did We Win the Week?'
        : 'No Metrics Reported'

  const handleSetWon = (won: boolean | null) => {
    if (!meeting_id || saving) return
    setSaving(true)
    router.post(`/meetings/${meeting_id}/set_won_the_week`, {
      won_the_week: won,
    }, {
      preserveScroll: true,
      onFinish: () => setSaving(false),
    })
  }

  const isManual = win_mode === 'manual'
  const showManualControls = isManual && can_facilitate && meeting_id

  return (
    <div
      style={{
        borderLeft: `4px solid ${borderColor}`,
        backgroundColor: bgColor,
        borderRadius: '8px',
        marginBottom: '24px',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <Icon size={28} color={textColor} strokeWidth={2.2} />
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '20px',
            fontWeight: 700,
            color: textColor,
            lineHeight: 1.3,
          }}
        >
          {label}
        </div>
        {win_mode === 'automatic' && !isNeutral && (
          <div
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              color: textColor,
              opacity: 0.85,
              marginTop: '2px',
            }}
          >
            ({win_count} of {total_count} metrics on target — {win_threshold}% needed to win)
          </div>
        )}
        {isManual && !isNeutral && (
          <div
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              color: textColor,
              opacity: 0.85,
              marginTop: '2px',
            }}
          >
            Marked by facilitator
          </div>
        )}
      </div>

      {showManualControls && (
        <div style={{ display: 'flex', gap: '8px', marginLeft: '12px', flexShrink: 0 }}>
          <button
            onClick={() => handleSetWon(won_the_week === true ? null : true)}
            disabled={saving}
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              padding: '8px 16px',
              borderRadius: '6px',
              border: won_the_week === true ? '2px solid #15532E' : '2px solid #E6E6E6',
              backgroundColor: won_the_week === true ? 'rgba(21,83,46,0.12)' : 'white',
              color: won_the_week === true ? '#15532E' : '#351E1D',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <CheckCircle size={16} />
            Won
          </button>
          <button
            onClick={() => handleSetWon(won_the_week === false ? null : false)}
            disabled={saving}
            style={{
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              padding: '8px 16px',
              borderRadius: '6px',
              border: won_the_week === false ? '2px solid #ED1C24' : '2px solid #E6E6E6',
              backgroundColor: won_the_week === false ? 'rgba(237,28,36,0.12)' : 'white',
              color: won_the_week === false ? '#ED1C24' : '#351E1D',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <XCircle size={16} />
            Lost
          </button>
        </div>
      )}
    </div>
  )
}
